import {
  PathsV1GroupUserUuidGetParametersQueryOrderByDirection,
  PathsV1GroupUserUuidGetParametersQueryOrderByField,
} from "@/api-schema-enums";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";

import { useGroupsByUserQuery } from "@/common/api/group/use-groups-by-user.query";
import { createRowNavigatePath } from "@/components/data-table/_helpers/create-row-navigate-path";
import { DataTable } from "@/components/data-table/data-table";
import { DataTableHeightMode, DataTableRow, ImplementedColumnDef } from "@/components/data-table/types";
import { UserGroupRow, userGroupTableDefaultColumns } from "./user-group-table-columns";

const DEFAULT_COLUMNS: ImplementedColumnDef<UserGroupRow, unknown>[] = [
  userGroupTableDefaultColumns.columnName as ImplementedColumnDef<UserGroupRow, unknown>,
  userGroupTableDefaultColumns.columnSources as ImplementedColumnDef<UserGroupRow, unknown>,
];

const DEFAULT_ORDER_BY = [
  {
    field: PathsV1GroupUserUuidGetParametersQueryOrderByField.Name,
    direction: PathsV1GroupUserUuidGetParametersQueryOrderByDirection.Asc,
  },
];

const navigateToGroup = createRowNavigatePath<UserGroupRow>("/ram/group");

export interface UserGroupTableProps {
  userUuid: string;
  heightMode?: DataTableHeightMode.Viewport | DataTableHeightMode.Content;
}

export function UserGroupTable({ userUuid, heightMode = DataTableHeightMode.Content }: UserGroupTableProps) {
  const { t } = useTranslation(["ram"]);
  const [_, navigate] = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [paginationState, setPaginationState] = useState({ page: 1, pageSize: 5 });
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);

  const { data, isPending, isError } = useGroupsByUserQuery(userUuid, { orderBy });

  const rows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const groups = data ?? [];
    return normalizedSearch ? groups.filter((group) => group.name.toLowerCase().includes(normalizedSearch)) : groups;
  }, [data, searchTerm]);

  const tableActions = useMemo(
    () => ({
      onSearchChange: (value: string) => {
        setSearchTerm(value);
        setPaginationState((prev) => ({ ...prev, page: 1 }));
      },
      onPaginationChange: setPaginationState,
      onSortingChange: (sorting: Array<{ field: string; direction: "asc" | "desc" }>) => {
        setOrderBy(
          sorting.map((s) => ({
            field: s.field as PathsV1GroupUserUuidGetParametersQueryOrderByField,
            direction: s.direction as PathsV1GroupUserUuidGetParametersQueryOrderByDirection,
          })),
        );
        setPaginationState((prev) => ({ ...prev, page: 1 }));
      },
      onRowClick: (row: DataTableRow<UserGroupRow>) => {
        const url = navigateToGroup(row.original);
        if (url) navigate(url);
      },
      onRowNavigate: (row: DataTableRow<UserGroupRow>) => navigateToGroup(row.original),
    }),
    [navigate],
  );

  return (
    <DataTable
      columns={DEFAULT_COLUMNS}
      data={rows}
      isLoading={isPending}
      isError={isError}
      heightMode={heightMode}
      searchValue={searchTerm}
      searchPlaceholder={t("ram:SearchForGroup")}
      initialSorting={[{ id: "name", desc: false }]}
      paginationState={paginationState}
      totalItems={rows.length}
      actions={tableActions}
      noDataTitle={t("ram:NoGroupsForThisUser")}
      features={{
        search: true,
        pagination: true,
        multiSelection: false,
        expansion: false,
      }}
    />
  );
}
